import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EventResponse } from '../types/Event';

interface RSVPButtonProps {
  response: EventResponse;
  isSelected: boolean;
  count: number;
  onRSVP: (response: EventResponse, email: string) => void;
  disabled?: boolean;
  eventId?: string;
}

const RSVPButton: React.FC<RSVPButtonProps> = ({
  response,
  isSelected,
  count,
  onRSVP,
  disabled = false,
  eventId,
}) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);

  const getButtonConfig = () => {
    switch (response) {
      case 'yes':
        return {
          emoji: '✅',
          color: '#4CAF50',
          label: t('event.responses.yes'),
        };
      case 'maybe':
        return {
          emoji: '🤔',
          color: '#FF9800',
          label: t('event.responses.maybe'),
        };
      case 'no':
        return {
          emoji: '❌',
          color: '#F44336',
          label: t('event.responses.no'),
        };
    }
  };

  const config = getButtonConfig();

  const handleClick = () => {
    if (disabled) return;
    setShowModal(true);
  };

  const isDemoEvent = !eventId || eventId === 'demo';

  const buttonStyle: React.CSSProperties = {
    backgroundColor: isSelected 
      ? config.color 
      : 'rgba(255, 255, 255, 0.08)',
    border: `1px solid ${isSelected ? config.color : 'rgba(255, 255, 255, 0.2)'}`,
    borderRadius: '20px',
    padding: '12px 20px',
    color: 'white',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    minWidth: '100px',
    justifyContent: 'center',
    boxShadow: isSelected ? `0 4px 16px ${config.color}40` : '0 2px 8px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease',
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
    fontWeight: '500',
    backdropFilter: 'blur(10px)',
  };

  return (
    <>
      <button
        style={buttonStyle}
        onClick={handleClick}
        disabled={disabled}
        onMouseEnter={(e) => {
          if (!disabled && !isSelected) {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.2)';
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled && !isSelected) {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
          }
        }}
      >
        <span style={{ fontSize: '18px' }}>{config.emoji}</span>
        <span style={{ 
          fontSize: '14px', 
          fontWeight: isSelected ? '600' : '500',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
        }}>
          {config.label}
        </span>
        <span style={{
          fontSize: '12px',
          opacity: 0.8,
          backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
          padding: '2px 6px',
          borderRadius: '8px',
          minWidth: '16px',
          textAlign: 'center',
        }}>
          {count}
        </span>
      </button>

      {showModal && (
        <div
          onClick={() => setShowModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '20px',
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            <h2 style={{
              margin: 0,
              fontSize: '20px',
              fontWeight: '600',
              color: '#1A1A2E',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            }}>
              {t('event.rsvp.modal.title')}
            </h2>
            <p style={{
              margin: 0,
              fontSize: '13px',
              color: '#666',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            }}>
              {isDemoEvent 
                ? t('event.rsvp.modal.demoText')
                : t('event.rsvp.modal.realText')}
            </p>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              alignItems: 'center',
            }}>
              <button
                onClick={() => {
                  window.open(
                    'https://apps.apple.com/us/app/allezhopp-event-planning/id6752710469',
                    '_blank'
                  );
                  setShowModal(false);
                }}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  borderRadius: '12px',
                  color: '#1A1A2E',
                  padding: '10px 20px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  width: 'fit-content',
                  minWidth: '200px',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                }}
              >
                <svg 
                  width="18" 
                  height="18" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ flexShrink: 0 }}
                >
                  <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.1 22C7.79 22.05 6.8 20.68 5.96 19.47C4.25 17 2.94 12.45 4.7 9.39C5.57 7.87 7.13 6.91 8.82 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 9.11 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z" fill="currentColor"/>
                </svg>
                {t('event.modal.appStore')}
              </button>
              <button
                onClick={() => {
                  window.open(
                    'https://play.google.com/store/apps/details?id=app.allezhopp.events&pcampaignid=web_share',
                    '_blank'
                  );
                  setShowModal(false);
                }}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  borderRadius: '12px',
                  color: '#1A1A2E',
                  padding: '10px 20px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  width: 'fit-content',
                  minWidth: '200px',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                }}
              >
                <svg 
                  width="18" 
                  height="18" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ flexShrink: 0 }}
                >
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" fill="currentColor"/>
                </svg>
                {t('event.modal.googlePlay')}
              </button>
            </div>
            <button
              onClick={() => setShowModal(false)}
              style={{
                backgroundColor: 'transparent',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                borderRadius: '12px',
                color: '#666',
                padding: '8px 16px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                marginTop: '0',
                width: 'fit-content',
                alignSelf: 'center',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.1)';
              }}
            >
              {t('event.modal.cancel')}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default RSVPButton;
